import React, { useContext, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { IoClose } from "react-icons/io5";
import { FaRegLightbulb } from "react-icons/fa";
import { UserContext } from "components/context/UserContext";
import LoginInput from "components/auth/common/LoginInput";
import SuccessModal from "components/client/dashboard/calendar/calendarRedesign/modals/common/SuccessModal";
import ErrorModal from "components/client/dashboard/calendar/calendarRedesign/modals/common/ErrorModal";
import { makeToast } from "helpers/common";
import ReportTypeSelector from "./components/ReportTypeSelector";
import ContextChip from "./components/ContextChip";
import SeverityPicker from "./components/SeverityPicker";
import ImageUploader from "./components/ImageUploader";
import GuestIdentityFields from "./components/GuestIdentityFields";
import { useCaresSubmit } from "./hooks/useCaresSubmit";
import { captureBrowserContext } from "./utils/captureContext";
import { CaresContextType, CaresReportType, CaresSeverity } from "./types";
import "./MyRepCaresPanel.css";

interface MyRepCaresPanelProps {
  fromDrawer?: boolean;
  hideTrigger?: boolean;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}

interface GuestIdentityState {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
}

const MyRepCaresPanel: React.FC<MyRepCaresPanelProps> = ({
  fromDrawer = false,
  hideTrigger = false,
  isOpen,
  onOpenChange,
}) => {
  const { state } = useContext(UserContext)!;
  const isLoggedIn = !!state?.currentUser?.id;
  const location = useLocation();
  const { submit, submitting } = useCaresSubmit();

  const isControlled = typeof isOpen === "boolean";
  const [internalOpen, setInternalOpen] = useState<boolean>(false);
  const showSideBar = isControlled ? !!isOpen : internalOpen;
  const setShowSideBar = (next: boolean | ((prev: boolean) => boolean)) => {
    const resolved =
      typeof next === "function"
        ? (next as (prev: boolean) => boolean)(showSideBar)
        : next;
    if (isControlled) {
      onOpenChange?.(resolved);
    } else {
      setInternalOpen(resolved);
      onOpenChange?.(resolved);
    }
  };

  const [type, setType] = useState<CaresReportType>("Suggestion");
  const [details, setDetails] = useState("");
  const [severity, setSeverity] = useState<CaresSeverity | undefined>(undefined);
  const [contextType, setContextType] = useState<CaresContextType>("page");
  const [images, setImages] = useState<File[]>([]);
  const [guestIdentity, setGuestIdentity] = useState<GuestIdentityState>({});
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const contextPath = useMemo(() => location.pathname, [location.pathname]);

  const handleGuestChange = (prop: string) => (value: string) => {
    setGuestIdentity((prev) => ({ ...prev, [prop]: value }));
  };

  const resetForm = () => {
    setType("Suggestion");
    setDetails("");
    setSeverity(undefined);
    setContextType("page");
    setImages([]);
    setGuestIdentity({});
  };

  const handleSubmit = async () => {
    if (!details.trim()) {
      makeToast("Please describe your bug, suggestion, or feedback.", "error");
      return;
    }
    if (!isLoggedIn && (!guestIdentity.firstName || !guestIdentity.lastName || !guestIdentity.email)) {
      makeToast("Please provide your first name, last name, and email.", "error");
      return;
    }

    const browserContext = captureBrowserContext();

    const response = await submit({
      type,
      details,
      severity: type === "Bug Report" ? severity : undefined,
      contextType,
      contextPath: contextType === "page" ? contextPath : undefined,
      userAgent: browserContext.userAgent,
      viewportWidth: browserContext.viewportWidth,
      viewportHeight: browserContext.viewportHeight,
      firstName: guestIdentity.firstName,
      lastName: guestIdentity.lastName,
      email: guestIdentity.email,
      phone: guestIdentity.phone,
      images,
    });

    if (response.isSuccessful) {
      resetForm();
      setShowSideBar(false);
      setSuccessMessage(
        response.message || "Thanks! Your report has been submitted.",
      );
    } else {
      setErrorMessage(
        response.error || "Something went wrong. Please try again.",
      );
    }
  };

  return (
    <>
      {!hideTrigger && (
        <button
          className={fromDrawer ? "drawer-nav-link" : "mrcp-fab-trigger"}
          onClick={() => setShowSideBar((prev) => !prev)}
        >
          {fromDrawer ? (
            <>
              <FaRegLightbulb style={{ width: 20, height: 20, margin: 0 }} />
              <span>MyRep Cares</span>
            </>
          ) : (
            "MyRep Cares"
          )}
        </button>
      )}

      <div className={`mrcp-sidebar custom-scroll-sm${showSideBar ? " mrcp-sidebar--open" : ""}`}>
        <div className="mrcp-content">
          <button className="mrcp-close-btn" onClick={() => setShowSideBar(false)}>
            <IoClose size={20} />
          </button>

          <p className="mrcp-title">MyRep Cares</p>
          <p className="mrcp-subtitle">
            Report a bug, share an idea, or tell us anything — we read every one.
          </p>

          <div className="mrcp-section">
            <p className="mrcp-label">What&apos;s this about?</p>
            <ReportTypeSelector value={type} onChange={setType} />
          </div>

          {type === "Bug Report" && (
            <div className="mrcp-section">
              <p className="mrcp-label">How severe is it?</p>
              <SeverityPicker value={severity} onChange={setSeverity} />
            </div>
          )}

          <div className="mrcp-section">
            <p className="mrcp-label">Is this about the page you&apos;re on, or something else?</p>
            <ContextChip
              contextType={contextType}
              contextPath={contextPath}
              onChange={setContextType}
            />
          </div>

          <div className="mrcp-section">
            <LoginInput
              label="Tell us more"
              value={details}
              name="details"
              onChange={() => setDetails}
              type="area"
              placeholder="Share as much detail as you can..."
            />
          </div>

          <div className="mrcp-section">
            <p className="mrcp-label">Attach a screenshot (optional)</p>
            <ImageUploader images={images} onChange={setImages} />
          </div>

          {!isLoggedIn && (
            <div className="mrcp-section">
              <GuestIdentityFields value={guestIdentity} onChange={handleGuestChange} />
            </div>
          )}

          <button
            className="mrcp-submit-btn"
            onClick={handleSubmit}
            disabled={submitting || !details.trim()}
          >
            {submitting ? "Submitting..." : "Submit"}
          </button>
        </div>
      </div>

      {showSideBar && (
        <div className="mrcp-overlay" onClick={() => setShowSideBar(false)} />
      )}

      <SuccessModal
        isOpen={!!successMessage}
        onClose={() => setSuccessMessage(null)}
        title="Thank you!"
        message={successMessage || ""}
      />
      <ErrorModal
        isOpen={!!errorMessage}
        onClose={() => setErrorMessage(null)}
        message={errorMessage || ""}
      />
    </>
  );
};

export default MyRepCaresPanel;
